import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Radio,
	RadioGroup,
	Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Which way the member's status would move, as reported by check-change-status.
export const REVERT_TO_PENDING_INTAKE = 1;
export const ADVANCE_TO_INTAKE_COMPLETED = 2;

interface Props {
	open: boolean;
	onClose: () => void;
	// 1 = the nature left code 5-7, so the member can go back to "Pending intake".
	// 2 = the nature entered code 5-7, so the member can move to "Intake completed".
	direction: number;
	// The nature behind the status change (the stored one for 1, the newly picked
	// one for 2), named in the message so the staff can see why it applies.
	nature?: string;
	onSave: (changeStatus: boolean) => void;
}

const ChangeMemberStatusDialog = ({
	open,
	onClose,
	direction,
	nature,
	onSave,
}: Props) => {
	const { t } = useTranslation();
	// Keeping the status in sync with the nature is what the edit implies, so it
	// is the default choice.
	const [changeStatus, setChangeStatus] = useState(true);

	// The dialog stays mounted between openings, so reset the choice each time.
	useEffect(() => {
		if (open) setChangeStatus(true);
	}, [open]);

	const isRevert = direction === REVERT_TO_PENDING_INTAKE;

	const tips = isRevert
		? nature
			? t('case.interventionEditRevertStatusTips', { nature })
			: t('case.interventionEditRevertStatusTipsWithoutNature')
		: nature
			? t('case.interventionEditCompleteStatusTips', { nature })
			: t('case.interventionEditCompleteStatusTipsWithoutNature');

	return (
		<Dialog fullWidth open={open} onClose={onClose}>
			<DialogTitle variant='h6'>
				{t('case.memberStatusConfirmation')}
			</DialogTitle>
			<DialogContent sx={{ paddingTop: 0 }}>
				<Box
					sx={{
						display: 'flex',
						gap: 1.5,
						padding: 2,
						borderRadius: 1,
						backgroundColor: 'chipPrimary.main',
					}}
				>
					<InfoOutlinedIcon
						color='primary'
						fontSize='small'
						sx={{ marginTop: '2px' }}
					/>
					<Box>
						{/* The reason and the question read as one sentence, so they share
						a single paragraph rather than competing for emphasis. */}
						<Typography variant='body2'>
							{tips}{' '}
							{isRevert
								? t('case.interventionEditRevertStatusQuestion')
								: t('case.interventionEditCompleteStatusQuestion')}
						</Typography>
						<RadioGroup
							value={changeStatus ? 'change' : 'keep'}
							onChange={(event) =>
								setChangeStatus(event.target.value === 'change')
							}
							sx={{ marginTop: 1 }}
						>
							<FormControlLabel
								value='change'
								control={<Radio size='small' />}
								label={
									<Typography variant='body2'>
										{isRevert
											? t('case.revertMemberStatusOption')
											: t('case.completeMemberStatusOption')}
									</Typography>
								}
							/>
							<FormControlLabel
								value='keep'
								control={<Radio size='small' />}
								label={
									<Typography variant='body2'>
										{isRevert
											? t('case.keepMemberStatusOption')
											: t('case.keepPendingMemberStatusOption')}
									</Typography>
								}
							/>
						</RadioGroup>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{t('button.cancel')}</Button>
				<Button
					variant='contained'
					onClick={() => {
						onSave(changeStatus);
						onClose();
					}}
				>
					{t('button.complete')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ChangeMemberStatusDialog;
